import { BooleanInput, Button, Container, Element, Label, NumericInput, SelectInput, SliderInput } from '@playcanvas/pcui';

import { Events } from '../events';
import { Distribution, estimateCount, ShapeKind } from '../generators';
import { AddShapeOptions, MAX_SPLATS, PREVIEW_MAX_SPLATS } from '../shape-handler';
import { i18n } from './localization';
import selectSphere from './svg/select-sphere.svg';

// soft warning threshold for the splat count display
const WARN_SPLATS = 500_000;

const createSvg = (svgString: string, args = {}) => {
    const decodedStr = decodeURIComponent(svgString.substring('data:image/svg+xml,'.length));
    return new Element({
        dom: new DOMParser().parseFromString(decodedStr, 'image/svg+xml').documentElement,
        ...args
    });
};

class ShapeDialog extends Container {
    show: () => Promise<AddShapeOptions | null>;
    hide: () => void;
    destroy: () => void;

    constructor(events: Events, args = {}) {
        args = {
            ...args,
            id: 'shape-dialog',
            class: ['settings-dialog', 'blocks-shortcuts'],
            hidden: true,
            tabIndex: -1
        };

        super(args);

        const dialog = new Container({
            id: 'dialog'
        });

        // header

        const headerIcon = createSvg(selectSphere, { id: 'icon' });
        const headerText = new Label({ id: 'text' });
        i18n.bindText(headerText, () => i18n.t('popup.shape.header').toUpperCase());
        const header = new Container({ id: 'header' });
        header.append(headerIcon);
        header.append(headerText);

        // helper to build a labeled row

        const makeRow = (localeKey: string, input: Element) => {
            const label = new Label({ class: 'label' });
            i18n.bindText(label, localeKey);
            const row = new Container({ class: 'row' });
            row.append(label);
            row.append(input);
            return row;
        };

        // points set via the placement tool, used by the point-defined kinds
        // (hull/polygon). selecting one of those kinds auto-activates the tool.
        let points: number[] = [];

        const isPointKind = (kind: string) => kind === 'hull' || kind === 'polygon';

        // shape kind

        const kindSelect = new SelectInput({
            class: 'select',
            defaultValue: 'sphere'
        });
        i18n.bindOptions(kindSelect, () => [
            { v: 'plane', t: i18n.t('popup.shape.kind.plane') },
            { v: 'disc', t: i18n.t('popup.shape.kind.disc') },
            { v: 'cube', t: i18n.t('popup.shape.kind.cube') },
            { v: 'sphere', t: i18n.t('popup.shape.kind.sphere') },
            { v: 'hull', t: i18n.t('popup.shape.kind.hull') },
            { v: 'polygon', t: i18n.t('popup.shape.kind.polygon') }
        ]);
        const kindRow = makeRow('popup.shape.kind', kindSelect);

        // dimensions

        const widthInput = new NumericInput({ class: 'number-input', value: 1, min: 0.001, precision: 3 });
        const widthRow = makeRow('popup.shape.width', widthInput);

        const heightInput = new NumericInput({ class: 'number-input', value: 1, min: 0.001, precision: 3 });
        const heightRow = makeRow('popup.shape.height', heightInput);

        const depthInput = new NumericInput({ class: 'number-input', value: 1, min: 0.001, precision: 3 });
        const depthRow = makeRow('popup.shape.depth', depthInput);

        const radiusInput = new NumericInput({ class: 'number-input', value: 0.5, min: 0.001, precision: 3 });
        const radiusRow = makeRow('popup.shape.radius', radiusInput);

        // filled / wall thickness

        const filledInput = new BooleanInput({ class: 'boolean', value: false });
        const filledRow = makeRow('popup.shape.filled', filledInput);

        const thicknessInput = new NumericInput({ class: 'number-input', value: 0, min: 0, precision: 3 });
        const thicknessRow = makeRow('popup.shape.thickness', thicknessInput);

        // density

        const densityInput = new NumericInput({ class: 'number-input', value: 20000, min: 1, max: 100_000_000, precision: 0 });
        const densityRow = makeRow('popup.shape.density', densityInput);

        // splat size

        const sizeAutoInput = new BooleanInput({ class: 'boolean', value: true });
        const sizeAutoRow = makeRow('popup.shape.size-auto', sizeAutoInput);

        const sizeInput = new NumericInput({ class: 'number-input', value: 0.01, min: 0.0001, precision: 4 });
        const sizeRow = makeRow('popup.shape.size', sizeInput);
        sizeRow.enabled = false;

        // distribution + seed

        const distributionSelect = new SelectInput({
            class: 'select',
            defaultValue: 'grid'
        });
        i18n.bindOptions(distributionSelect, () => [
            { v: 'grid', t: i18n.t('popup.shape.distribution.grid') },
            { v: 'exact', t: i18n.t('popup.shape.distribution.exact') },
            { v: 'random', t: i18n.t('popup.shape.distribution.random') }
        ]);
        const distributionRow = makeRow('popup.shape.distribution', distributionSelect);

        const seedInput = new NumericInput({ class: 'number-input', value: 42, min: 0, precision: 0 });
        const seedRow = makeRow('popup.shape.seed', seedInput);

        // size / color variation

        const sizeJitterSlider = new SliderInput({ class: 'slider', min: 0, max: 100, precision: 0, value: 0 });
        const sizeJitterRow = makeRow('popup.shape.size-jitter', sizeJitterSlider);

        const colorJitterSlider = new SliderInput({ class: 'slider', min: 0, max: 100, precision: 0, value: 0 });
        const colorJitterRow = makeRow('popup.shape.color-jitter', colorJitterSlider);

        // color + opacity

        // native color input (like the SplatAdmin background picker): opens
        // the OS color dialog with palette, hex entry and eyedropper. floats
        // 0-1 <-> #rrggbb conversion keeps the rest of the dialog hex-free.
        const floatsToHex = (c: { r: number, g: number, b: number }) => {
            return `#${[c.r, c.g, c.b].map(v => Math.round(Math.min(1, Math.max(0, v)) * 255).toString(16).padStart(2, '0')).join('')}`;
        };
        const hexToFloats = (hex: string) => {
            const n = parseInt(hex.slice(1), 16);
            return { r: ((n >> 16) & 255) / 255, g: ((n >> 8) & 255) / 255, b: (n & 255) / 255 };
        };

        const colorInput = document.createElement('input');
        colorInput.type = 'color';
        colorInput.value = '#ff661a';
        const colorHex = document.createElement('span');
        colorHex.className = 'color-hex';
        colorHex.textContent = colorInput.value;
        const colorWrap = document.createElement('div');
        colorWrap.className = 'color-input-wrap';
        colorWrap.append(colorInput, colorHex);
        const colorRow = makeRow('popup.shape.color', new Element({ dom: colorWrap }));

        const opacitySlider = new SliderInput({ class: 'slider', min: 0, max: 100, precision: 0, value: 100 });
        const opacityRow = makeRow('popup.shape.opacity', opacitySlider);

        // flat splats

        const flatInput = new BooleanInput({ class: 'boolean', value: true });
        const flatRow = makeRow('popup.shape.flat', flatInput);

        // splat count display

        const countLabel = new Label({ id: 'shape-count-label' });
        const countRow = new Container({ class: 'row' });
        countRow.append(countLabel);

        // content

        const content = new Container({ id: 'content' });
        content.append(kindRow);
        content.append(widthRow);
        content.append(heightRow);
        content.append(depthRow);
        content.append(radiusRow);
        content.append(filledRow);
        content.append(thicknessRow);
        content.append(densityRow);
        content.append(sizeAutoRow);
        content.append(sizeRow);
        content.append(distributionRow);
        content.append(seedRow);
        content.append(sizeJitterRow);
        content.append(colorJitterRow);
        content.append(colorRow);
        content.append(opacityRow);
        content.append(flatRow);
        content.append(countRow);

        // footer

        const footer = new Container({ id: 'footer' });

        const cancelButton = new Button({ class: 'button' });
        i18n.bindText(cancelButton, 'popup.cancel');

        const insertButton = new Button({ class: 'button' });
        i18n.bindText(insertButton, 'popup.shape.insert');

        footer.append(cancelButton);
        footer.append(insertButton);

        dialog.append(header);
        dialog.append(content);
        dialog.append(footer);

        this.append(dialog);

        // assemble the current parameter set from the ui state

        const currentParams = (): AddShapeOptions => {
            const kind = kindSelect.value as ShapeKind;
            const volume = filledInput.value && (kind === 'cube' || kind === 'sphere' || kind === 'hull');
            const { r, g, b } = hexToFloats(colorInput.value);
            return {
                kind,
                points: isPointKind(kind) ? points.slice() : undefined,
                width: widthInput.value,
                height: heightInput.value,
                depth: depthInput.value,
                radius: radiusInput.value,
                filled: filledInput.value,
                thickness: volume ? 0 : thicknessInput.value,
                density: densityInput.value,
                splatSize: sizeAutoInput.value ? 'auto' : sizeInput.value,
                color: { r, g, b },
                opacity: opacitySlider.value / 100,
                distribution: distributionSelect.value as Distribution,
                seed: seedInput.value,
                sizeJitter: sizeJitterSlider.value / 100,
                colorJitter: colorJitterSlider.value / 100,
                flatSplats: !volume && flatInput.value
            };
        };

        // sync row visibility and the splat count to the current state

        // activate the point placement tool while a point-defined kind is
        // selected in the visible dialog, deactivate it otherwise
        const syncPointTool = () => {
            const wantTool = !this.hidden && isPointKind(kindSelect.value);
            const activeTool = events.invoke('tool.active');
            if (wantTool && activeTool !== 'pointPlace') {
                events.fire('tool.pointPlace');
            } else if (!wantTool && activeTool === 'pointPlace') {
                events.fire('tool.deactivate');
            }
        };

        const sync = () => {
            const kind = kindSelect.value as ShapeKind;
            const pointKind = isPointKind(kind);
            const hasFill = kind === 'cube' || kind === 'sphere' || kind === 'hull';
            const volume = hasFill && filledInput.value;

            widthRow.hidden = !(kind === 'plane' || kind === 'cube');
            depthRow.hidden = !(kind === 'plane' || kind === 'cube');
            heightRow.hidden = kind !== 'cube';
            radiusRow.hidden = !(kind === 'disc' || kind === 'sphere');
            filledRow.hidden = !hasFill;
            thicknessRow.hidden = !hasFill || volume;
            flatRow.hidden = volume;
            sizeRow.enabled = !sizeAutoInput.value;

            syncPointTool();

            const count = estimateCount(currentParams());
            if (pointKind && points.length < 9) {
                countLabel.text = i18n.t('popup.shape.need-points');
                countLabel.dom.style.color = '#fa0';
                insertButton.enabled = false;
                if (!this.hidden) {
                    events.fire('shape.dialogChanged', currentParams());
                }
                return;
            }
            if (count > MAX_SPLATS) {
                countLabel.text = i18n.t('popup.shape.count-max', { max: MAX_SPLATS.toLocaleString() });
            } else if (count > PREVIEW_MAX_SPLATS) {
                countLabel.text = `${i18n.t('popup.shape.count', { count: count.toLocaleString() })} — ${i18n.t('popup.shape.preview-paused')}`;
            } else {
                countLabel.text = i18n.t('popup.shape.count', { count: count.toLocaleString() });
            }
            countLabel.dom.style.color = count > MAX_SPLATS ? '#f44' : (count > WARN_SPLATS ? '#fa0' : '');
            insertButton.enabled = count > 0 && count <= MAX_SPLATS;

            // drive the live preview, but only while the dialog is visible
            if (!this.hidden) {
                events.fire('shape.dialogChanged', currentParams());
            }
        };

        for (const input of [kindSelect, widthInput, heightInput, depthInput, radiusInput, filledInput,
            thicknessInput, densityInput, sizeAutoInput, sizeInput, distributionSelect, seedInput,
            sizeJitterSlider, colorJitterSlider, opacitySlider, flatInput]) {
            input.on('change', sync);
        }

        // 'input' fires continuously while picking, so the preview follows
        // the color live
        colorInput.addEventListener('input', () => {
            colorHex.textContent = colorInput.value;
            sync();
        });

        // handle key bindings for enter and escape

        let onCancel: () => void;
        let onInsert: () => void;

        cancelButton.on('click', () => onCancel());
        insertButton.on('click', () => onInsert());

        const keydown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                e.preventDefault();
                e.stopPropagation();
                onCancel();
            } else if (e.key === 'Enter' && insertButton.enabled) {
                e.preventDefault();
                e.stopPropagation();
                onInsert();
            }
        };

        // restore the last-used parameters (values are set while the dialog
        // is hidden, so no preview events fire)

        const STORAGE_KEY = 'supersplat.shape.lastParams';

        const restoreParams = () => {
            try {
                const stored = JSON.parse(localStorage.getItem(STORAGE_KEY) ?? 'null');
                if (!stored) {
                    return;
                }
                kindSelect.value = stored.kind ?? 'sphere';
                widthInput.value = stored.width ?? 1;
                heightInput.value = stored.height ?? 1;
                depthInput.value = stored.depth ?? 1;
                radiusInput.value = stored.radius ?? 0.5;
                filledInput.value = !!stored.filled;
                thicknessInput.value = stored.thickness ?? 0;
                densityInput.value = stored.density ?? 20000;
                sizeAutoInput.value = stored.splatSize === 'auto' || stored.splatSize === undefined;
                if (typeof stored.splatSize === 'number') {
                    sizeInput.value = stored.splatSize;
                }
                distributionSelect.value = stored.distribution ?? 'grid';
                seedInput.value = stored.seed ?? 42;
                sizeJitterSlider.value = (stored.sizeJitter ?? 0) * 100;
                colorJitterSlider.value = (stored.colorJitter ?? 0) * 100;
                colorInput.value = floatsToHex({ r: stored.color?.r ?? 1, g: stored.color?.g ?? 0.4, b: stored.color?.b ?? 0.1 });
                colorHex.textContent = colorInput.value;
                opacitySlider.value = (stored.opacity ?? 1) * 100;
                flatInput.value = stored.flatSplats ?? true;
            } catch (e) {
                // ignore corrupt storage
            }
        };

        const storeParams = () => {
            try {
                const params: Record<string, unknown> = { ...currentParams() };
                delete params.points;
                const previous = JSON.parse(localStorage.getItem(STORAGE_KEY) ?? '{}');
                localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...previous, ...params }));
            } catch (e) {
                // storage full/unavailable: not critical
            }
        };

        // follow point edits from the placement tool while previewing
        events.on('pointPlace.changed', (flat: number[]) => {
            if (!this.hidden && isPointKind(kindSelect.value)) {
                points = flat.slice();
                sync();
            }
        });

        this.show = () => {
            // the placement tool owns the points; they survive dialog cancels
            points = events.invoke('pointPlace.points') ?? [];

            restoreParams();

            this.hidden = false;
            events.fire('shape.previewBegin');
            sync();

            document.addEventListener('keydown', keydown);
            this.dom.focus();

            return new Promise<AddShapeOptions | null>((resolve) => {
                onCancel = () => {
                    // remember the settings so reopening continues where the
                    // user left off (points survive cancel anyway)
                    storeParams();
                    resolve(null);
                };
                onInsert = () => {
                    storeParams();
                    resolve(currentParams());
                };
            }).finally(() => {
                document.removeEventListener('keydown', keydown);
                this.hide();
            });
        };

        this.hide = () => {
            this.hidden = true;
            syncPointTool();
        };

        this.destroy = () => {
            this.hide();
            super.destroy();
        };
    }
}

export { ShapeDialog };
